import { DestroyRef, Injectable, inject } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { ProfileDataService } from './profile-data.service';
import { dueReminders } from '../desktop-presence';
import { notify } from '../tauri-dialog';
import { isTauri } from '../tauri-runtime';

const TICK_MS = 30_000;
const FIRED_KEY = 'im-desktop-reminders-fired';

/**
 * DesktopReminderService — reminders the browser tab cannot give.
 *
 * RESPONSIBILITY: While the app is resident (window hidden or not), raise a
 * native notification five minutes before a meeting and when a desk's
 * check-in window opens. Server-pushed critical/high notifications are
 * already mirrored natively by NotificationCenterService; these two are
 * computed from today's bookings on the client because nothing pushes them.
 *
 * USED BY: AppShellComponent (desktop only). Fired keys persist in
 * localStorage so a relaunch never repeats a reminder.
 */
@Injectable({ providedIn: 'root' })
export class DesktopReminderService {
  private readonly profile = inject(ProfileDataService);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;

  start(): void {
    if (!isTauri || this.started) return;
    this.started = true;
    const tick = setInterval(() => void this.tick(), TICK_MS);
    this.destroyRef.onDestroy(() => clearInterval(tick));
    void this.tick();
  }

  private async tick(): Promise<void> {
    const fired = this.readFired();
    const due = dueReminders(
      this.profile.meetingBookings(),
      this.profile.deskBooking(),
      Date.now(),
      new Set(Object.keys(fired)),
    );
    if (due.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    for (const reminder of due) {
      fired[reminder.key] = today;
      this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_REMINDER_FIRED, { kind: reminder.kind });
      await notify({ title: reminder.title, body: reminder.body });
    }
    this.writeFired(fired, today);
  }

  private readFired(): Record<string, string> {
    try {
      const raw = localStorage.getItem(FIRED_KEY);
      return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  /** Keep only today's keys — booking ids never recur across days. */
  private writeFired(fired: Record<string, string>, today: string): void {
    const pruned = Object.fromEntries(Object.entries(fired).filter(([, d]) => d === today));
    try {
      localStorage.setItem(FIRED_KEY, JSON.stringify(pruned));
    } catch {
      /* storage unavailable — worst case a reminder repeats after relaunch */
    }
  }
}
